import { X, CheckCircle, XCircle, Clock, Send, User, Calendar, FileText } from 'lucide-react';
import { CashSettlement, CashSettlementStatus } from '@/services/cashSettlementApi';
import { format } from 'date-fns';
import { getCurrencySymbol } from '@/utils/currency';

interface SettlementDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  settlement: CashSettlement | null;
  onVerify?: (settlement: CashSettlement) => void;
  onReject?: (settlement: CashSettlement) => void;
}

const STATUS_CONFIG: Record<CashSettlementStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  PENDING: { label: 'Pending', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: Clock },
  SUBMITTED: { label: 'Submitted', color: 'text-blue-700', bg: 'bg-blue-100', icon: Send },
  VERIFIED: { label: 'Verified', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-100', icon: XCircle },
};

const DENOMINATION_LABELS: Record<string, { label: string; value: number }> = {
  note2000Count: { label: '2000', value: 2000 },
  note500Count: { label: '500', value: 500 },
  note200Count: { label: '200', value: 200 },
  note100Count: { label: '100', value: 100 },
  note50Count: { label: '50', value: 50 },
  note20Count: { label: '20', value: 20 },
  note10Count: { label: '10', value: 10 },
  coin5Count: { label: '5', value: 5 },
  coin2Count: { label: '2', value: 2 },
  coin1Count: { label: '1', value: 1 },
};

export default function SettlementDetailModal({
  isOpen,
  onClose,
  settlement,
  onVerify,
  onReject,
}: SettlementDetailModalProps) {
  if (!isOpen || !settlement) return null;

  const statusConfig = STATUS_CONFIG[settlement.status];
  const StatusIcon = statusConfig.icon;
  const canAct = settlement.status === 'SUBMITTED';
  const diff = Number(settlement.cashDifference || 0);

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleBackdrop}>
      <div className="fixed inset-0 bg-black bg-opacity-50" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{settlement.settlementNumber}</h3>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(settlement.settlementDate)}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {settlement.branch?.name || 'Unknown Branch'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {statusConfig.label}
              </span>
              <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Settled By */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>Settled by: <strong className="text-gray-900">{settlement.settledBy?.name || '-'}</strong></span>
            </div>

            {/* Financial Summary */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Financial Summary</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <SummaryItem label="Total Collected" value={settlement.totalCollected} color="text-green-700" />
                <SummaryItem label="Total Refunds" value={settlement.totalRefunds} color="text-red-600" />
                <SummaryItem label="Total Expenses" value={settlement.totalExpenses} color="text-orange-600" />
                <SummaryItem label="Net Cash" value={settlement.netCashAmount} color="text-blue-700" bold />
                <SummaryItem label="Physical Count" value={settlement.physicalCashCount} color="text-purple-700" />
                <div className="text-center">
                  <p className="text-xs text-gray-500">Difference</p>
                  <p className={`text-sm font-bold ${diff === 0 ? 'text-green-600' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {diff === 0 ? '0 (Match)' : `${getCurrencySymbol()}${Math.abs(diff).toLocaleString()} ${diff > 0 ? 'Over' : 'Short'}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Method Breakdown */}
            {settlement.methodBreakdowns && settlement.methodBreakdowns.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment Method Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Method</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Opening</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Collected</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Refunds</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Expenses</th>
                        <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">Closing</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {settlement.methodBreakdowns.map((m) => (
                        <tr key={m.id}>
                          <td className="py-2 px-2 font-medium text-gray-900">{m.paymentMethod?.name || '-'}</td>
                          <td className="py-2 px-2 text-right text-gray-600">{getCurrencySymbol()}{Number(m.openingBalance).toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-green-700">{getCurrencySymbol()}{Number(m.collectedAmount).toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-red-600">{getCurrencySymbol()}{Number(m.refundedAmount).toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-orange-600">{getCurrencySymbol()}{Number(m.expenseAmount).toLocaleString()}</td>
                          <td className="py-2 px-2 text-right font-semibold text-gray-900">{getCurrencySymbol()}{Number(m.closingBalance).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cash Denominations */}
            {settlement.denominations && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Cash Denominations</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(DENOMINATION_LABELS).map(([key, { label, value }]) => {
                    const count = Number((settlement.denominations as Record<string, number>)?.[key] || 0);
                    if (count === 0) return null;
                    return (
                      <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                        <span className="text-gray-600">{getCurrencySymbol()}{label} x {count}</span>
                        <span className="font-medium text-gray-900">{getCurrencySymbol()}{(value * count).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-right text-sm font-bold text-gray-900">
                  Total: {getCurrencySymbol()}{Number(settlement.denominations.totalAmount).toLocaleString()}
                </div>
              </div>
            )}

            {/* Notes */}
            {settlement.notes && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1.5">
                  <FileText className="w-4 h-4" /> Notes
                </h4>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{settlement.notes}</p>
              </div>
            )}

            {/* Verification/Rejection Info */}
            {settlement.status === 'VERIFIED' && settlement.verifiedBy && (
              <div className="bg-green-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-green-800">
                  Verified by {settlement.verifiedBy.name}
                  {settlement.verifiedAt && ` on ${formatDate(settlement.verifiedAt)}`}
                </p>
                {settlement.verificationNotes && (
                  <p className="text-green-700 mt-1">{settlement.verificationNotes}</p>
                )}
              </div>
            )}

            {settlement.status === 'REJECTED' && settlement.rejectedBy && (
              <div className="bg-red-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-red-800">
                  Rejected by {settlement.rejectedBy.name}
                  {settlement.rejectedAt && ` on ${formatDate(settlement.rejectedAt)}`}
                </p>
                {settlement.rejectionReason && (
                  <p className="text-red-700 mt-1">Reason: {settlement.rejectionReason}</p>
                )}
              </div>
            )}

            {/* Action History */}
            {settlement.actions && settlement.actions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Action History</h4>
                <div className="space-y-2">
                  {settlement.actions.map((action) => (
                    <div
                      key={action.id}
                      className={`flex items-start gap-3 rounded-lg p-3 text-sm ${
                        action.actionType === 'VERIFIED' ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      {action.actionType === 'VERIFIED' ? (
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <p className={action.actionType === 'VERIFIED' ? 'text-green-800' : 'text-red-800'}>
                          <strong>{action.actionType}</strong> by {action.performedBy?.name || 'Unknown'}
                          {action.performedAt && ` - ${formatDate(action.performedAt)}`}
                        </p>
                        {action.notes && <p className="mt-0.5 text-gray-600">{action.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          {canAct && (onVerify || onReject) && (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
              {onReject && (
                <button
                  onClick={() => onReject(settlement)}
                  className="px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Reject
                </button>
              )}
              {onVerify && (
                <button
                  onClick={() => onVerify(settlement)}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Verify
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryItem({ label, value, color, bold }: { label: string; value: number; color: string; bold?: boolean }) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-sm ${bold ? 'font-bold' : 'font-semibold'} ${color}`}>
        {getCurrencySymbol()}{Number(value || 0).toLocaleString()}
      </p>
    </div>
  );
}
